import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './config';
import { buildListQuery, ListQueryOptions, normalizePaginatedListResponse, PaginatedApiResponse } from '../../../services/paginatedList';

export interface AccAccount {
  id: number;
  parentId?: number | null;
  code: string;
  combLevel?: string;
  name: string;
  isPosting: boolean;
  allowOpenItems: boolean;
  paymentTermsDays?: number | null;
  departmentId?: number | null;
  accountTypeId?: number | null;
  operationalTypeId?: number | null;
  isIntercompany: boolean;
  isBudget: boolean;
  isActive: boolean;
  sortOrder: number;
  parent?: { id: number; code: string; name: string } | null;
  department?: { id: number; code: string; name: string } | null;
  accountType?: { id: number; code: string; name: string } | null;
  operationalType?: { id: number; code: string; name: string } | null;
  /** Resolved operational type from self or ancestor groups. */
  effectiveOperationalType?: { id: number; code: string; name: string } | null;
  /** @deprecated use effectiveOperationalType */
  effectiveAccountType?: { id: number; code: string; name: string } | null;
  currencyIds?: number[];
  companyIds?: number[];
  allowedCurrencies?: Array<{
    currencyId: number;
    currency?: { id: number; code: string; name: string; symbol?: string | null };
  }>;
  allowedCompanies?: Array<{
    companyId: number;
    company?: { id: number; code: string; name: string };
  }>;
  _count?: { children: number; documentLines?: number };
  usedInRecords?: boolean;
  createdAt: string;
  updatedAt: string;
}

export type AccAccountTreeNode = AccAccount & { children: AccAccountTreeNode[] };

export const useAccAccounts = (searchParams?: string, page = 1, limit = 25, options?: ListQueryOptions) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<PaginatedApiResponse<AccAccount> | AccAccount[]>(
    `${moduleConfig.module.api.list}${q}`,
    fetcher,
  );
  const normalized = normalizePaginatedListResponse<AccAccount>(data, page, limit);
  return { data: normalized.data, total: normalized.total, isLoading, isError: error, mutate };
};

export const useAccAccount = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<AccAccount>(id ? moduleConfig.module.api.detail(id) : null, fetcher);
  return { data, item: data, isLoading, isError: error, mutate };
};

export const useAccAccountTree = (options?: { lite?: boolean }) => {
  const lite = options?.lite ? '?lite=1' : '';
  const { data, error, isLoading, mutate } = useSWR<AccAccountTreeNode[]>(
    `/acc-accounts/tree${lite}`,
    fetcher,
  );
  return { data: data ?? [], isLoading, isError: error, mutate };
};
